// =====================================================================
// MetaLabEnhancements.jsx (FULL FILE - UPDATED WITH ARCANA FROM EXCEL)
// - RadarChart, SkillComboCalculator
// - HeroCompareTab: ALL-IN-ONE (Compare + Build + 1v1)
// - Arcana: ใช้ข้อมูลจาก Excel (patch.runes) แทน hardcode
// =====================================================================

import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Zap, Sword, Shield, Heart, Users,
  GitCompare, Target,
  RotateCcw,
} from "lucide-react";

import {
  computeTotalStatsRealistic,
  applyDefenseWithPenetration,
} from "./statsCalculator";

// =====================================================================
// CONSTANTS & HELPERS
// =====================================================================

const STAT_LABELS = {
  hp: "HP",
  phys_atk: "ATK",
  magic_power: "AP",
  phys_def: "Phys DEF",
  magic_def: "Magic DEF",
  move_speed: "Move Speed",
  move_speed_pct: "Move Speed %",
  max_mana: "Max Mana",
  armor_pierce: "Armor Pierce",
  armor_pierce_pct: "Armor Pierce %",
  magic_pierce: "Magic Pierce",
  magic_pierce_pct: "Magic Pierce %",
  attack_speed: "Attack Speed %",
  crit_rate: "Crit Rate %",
  crit_dmg: "Crit Damage %",
  life_steal: "Life Steal %",
  magic_life_steal: "Magic Life Steal %",
  cooldown_reduction: "CDR %",
  hp_regen: "HP Regen",
  mana_regen: "Mana Regen",
};

const RADAR_STATS = [
  { key: "hp", label: "HP", max: 15000, color: "#ef4444" },
  { key: "phys_atk", label: "ATK", max: 600, color: "#f59e0b" },
  { key: "magic_power", label: "AP", max: 1200, color: "#8b5cf6" },
  { key: "phys_def", label: "DEF", max: 800, color: "#64748b" },
  { key: "magic_def", label: "M.DEF", max: 500, color: "#06b6d4" },
  { key: "move_speed", label: "SPD", max: 500, color: "#10b981" },
];

const ROLES = {
  Fighter: { color: "#f59e0b", icon: Sword },
  Mage: { color: "#8b5cf6", icon: Zap },
  Tank: { color: "#64748b", icon: Shield },
  Support: { color: "#10b981", icon: Heart },
  Assassin: { color: "#ef4444", icon: Target },
  Carry: { color: "#0ea5e9", icon: Target },
};

// =====================================================================
// ARCANA HELPERS (ใช้ข้อมูลจาก Excel)
// =====================================================================

const ARCANA_COLORS = {
  Red: { label: "Red Arcana (ATK)", color: "#ef4444", icon: Sword },
  Purple: { label: "Purple Arcana (HP/Sustain)", color: "#a855f7", icon: Zap },
  Green: { label: "Green Arcana (DEF)", color: "#22c55e", icon: Shield },
};

const filterArcana = (arcanas, color) =>
  (arcanas || []).filter(a =>
    a.type === "ARCANA" &&
    String(a.color).toLowerCase() === color.toLowerCase() &&
    a.tier === 3
  );

const sumArcanaStats = (arcanaSet, qty = 10) => {
  const out = {};
  for (const key of ["red", "purple", "green"]) {
    const arcana = arcanaSet?.[key];
    if (!arcana?.stats) continue;
    for (const [k, v] of Object.entries(arcana.stats)) {
      out[k] = (out[k] || 0) + (+v || 0) * qty;
    }
  }
  return out;
};

// =====================================================================
// HERO & ITEM HELPERS
// =====================================================================

const DEFAULT_LEVELS = [...Array(15)].map((_, i) => i + 1);

const getHeroLevels = (hero) => {
  const levels = Object.keys(hero?.levels || {})
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  return levels.length ? levels : DEFAULT_LEVELS;
};

const getHeroLevelObj = (hero, level) =>
  hero?.levels?.[level] ||
  hero?.levels?.[1] ||
  Object.values(hero?.levels || {})[0] ||
  null;

const toStatsItem = (it) => {
  if (!it) return it;
  if (it.stats && typeof it.stats === "object") return it;
  if (it.effects && typeof it.effects === "object") return { ...it, stats: it.effects };
  if (it.bonus && typeof it.bonus === "object") return { ...it, stats: it.bonus };
  if (it.attributes && typeof it.attributes === "object") return { ...it, stats: it.attributes };
  return it;
};

const normalizeSelected = (selected, allItems) => {
  if (!Array.isArray(selected) || selected.length === 0) return [];
  const first = selected[0];
  if (typeof first === "string" || typeof first === "number") {
    return selected
      .map((id) => allItems.find((it) => it.id === id || it.name === id || it.key === id))
      .filter(Boolean)
      .map(toStatsItem);
  }
  return selected.map(toStatsItem);
};

const sumItemStats = (items = []) => {
  const out = {};
  for (const it of items) {
    const s = it?.stats || {};
    for (const [k, v] of Object.entries(s)) out[k] = (out[k] || 0) + (+v || 0);
  }
  return out;
};

const computeTotalStatsFallback = (heroLevelObj, items = []) => {
  const base = { ...(heroLevelObj || {}) };
  const bonus = sumItemStats(items);
  const total = { ...base };
  for (const [k, v] of Object.entries(bonus)) total[k] = (+total[k] || 0) + (+v || 0);
  return { base, bonus, total };
};

const applyDefenseSimple = (raw, def) => (+raw || 0) * (600 / (600 + Math.max(0, +def || 0)));

const computeFinalStats = (heroLevelObj, items = [], runes = []) => {
  const merged = [...(items || []), ...(runes || [])];

  if (typeof computeTotalStatsRealistic === "function") {
    try {
      const res = computeTotalStatsRealistic(heroLevelObj, items, runes);
      const out = res?.total || res;
      if (out && typeof out === "object") return out;
    } catch (e) {}

    try {
      const res = computeTotalStatsRealistic(heroLevelObj, merged);
      const out = res?.total || res;
      if (out && typeof out === "object") return out;
    } catch (e) {}

    try {
      const res = computeTotalStatsRealistic(heroLevelObj);
      const out = res?.total || res;
      if (out && typeof out === "object") return out;
    } catch (e) {}
  }

  return computeTotalStatsFallback(heroLevelObj, merged).total;
};

// =====================================================================
// UI COMPONENTS
// =====================================================================

const Card = ({ children, neon = false, style = {} }) => (
  <div
    style={{
      background: "rgba(0, 0, 0, 0.9)",
      backdropFilter: "blur(20px)",
      borderRadius: "16px",
      border: neon ? "2px solid #ff1026" : "1px solid rgba(255,16,38,0.2)",
      boxShadow: neon ? "0 0 30px rgba(255,16,38,0.4)" : "none",
      position: "relative",
      transition: "all 0.3s",
      ...style,
    }}
  >
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", style = {}, disabled = false }) => {
  const [hover, setHover] = useState(false);
  const variants = {
    primary: {
      background: hover
        ? "linear-gradient(135deg, #ff4d5f, #ff1026)"
        : "linear-gradient(135deg, #ff1026, #a30013)",
      color: "#fff",
      border: "none",
    },
    secondary: {
      background: hover ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.6)",
      color: hover ? "#fff" : "#9ca3af",
      border: hover ? "2px solid #ff1026" : "2px solid rgba(255,16,38,0.3)",
    },
    danger: {
      background: "rgba(239,68,68,0.2)",
      color: "#ef4444",
      border: "2px solid rgba(239,68,68,0.5)",
    },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: "10px 14px",
        borderRadius: "12px",
        fontWeight: 800,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "'Rajdhani', sans-serif",
        fontSize: "13px",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        justifyContent: "center",
        opacity: disabled ? 0.5 : 1,
        transition: "all 0.3s",
        ...variants[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
};

const Select = ({ value, onChange, options, style = {} }) => (
  <select
    value={value}
    onChange={onChange}
    style={{
      width: "100%",
      padding: "10px 12px",
      background: "rgba(0,0,0,0.6)",
      border: "2px solid rgba(255,16,38,0.3)",
      borderRadius: "12px",
      color: "#fff",
      fontSize: "14px",
      fontFamily: "'Rajdhani', sans-serif",
      appearance: "none",
      cursor: "pointer",
      ...style,
    }}
  >
    {options.map((o) => (
      <option key={String(o.value)} value={o.value} style={{ background: "#000" }}>
        {o.label}
      </option>
    ))}
  </select>
);

// =====================================================================
// 1) RADAR CHART
// =====================================================================

export const RadarChart = ({
  stats = {},
  stats2 = null,
  size = 280,
  label1 = "Hero 1",
  label2 = "Hero 2",
  color1 = "#ff1026",
  color2 = "#22c55e",
}) => {
  const center = size / 2;
  const radius = (size - 60) / 2;

  const getPoint = (index, value, maxValue) => {
    const angle = (Math.PI * 2 * index) / RADAR_STATS.length - Math.PI / 2;
    const r = (value / maxValue) * radius;
    return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) };
  };

  const createPath = (statsObj) => {
    const points = RADAR_STATS.map((s, i) =>
      getPoint(i, Math.min(statsObj[s.key] || 0, s.max), s.max)
    );
    return points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ") + " Z";
  };

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ overflow: "visible" }}>
        {[0.2, 0.4, 0.6, 0.8, 1.0].map((level, li) => (
          <polygon
            key={li}
            points={RADAR_STATS.map((_, i) => {
              const angle = (Math.PI * 2 * i) / RADAR_STATS.length - Math.PI / 2;
              return `${center + radius * level * Math.cos(angle)},${center + radius * level * Math.sin(angle)}`;
            }).join(" ")}
            fill="none"
            stroke="rgba(255,16,38,0.15)"
            strokeWidth="1"
          />
        ))}

        {RADAR_STATS.map((_, i) => {
          const angle = (Math.PI * 2 * i) / RADAR_STATS.length - Math.PI / 2;
          return (
            <line
              key={i}
              x1={center}
              y1={center}
              x2={center + radius * Math.cos(angle)}
              y2={center + radius * Math.sin(angle)}
              stroke="rgba(255,16,38,0.2)"
            />
          );
        })}

        {stats2 && <path d={createPath(stats2)} fill={`${color2}30`} stroke={color2} strokeWidth="2" />}
        <path d={createPath(stats)} fill={`${color1}30`} stroke={color1} strokeWidth="2" />

        {RADAR_STATS.map((s, i) => {
          const point = getPoint(i, Math.min(stats[s.key] || 0, s.max), s.max);
          return <circle key={i} cx={point.x} cy={point.y} r="5" fill={color1} stroke="#fff" strokeWidth="2" />;
        })}

        {stats2 &&
          RADAR_STATS.map((s, i) => {
            const point = getPoint(i, Math.min(stats2[s.key] || 0, s.max), s.max);
            return <circle key={`b-${i}`} cx={point.x} cy={point.y} r="4" fill={color2} stroke="#fff" strokeWidth="2" />;
          })}

        {RADAR_STATS.map((s, i) => {
          const angle = (Math.PI * 2 * i) / RADAR_STATS.length - Math.PI / 2;
          return (
            <text
              key={i}
              x={center + (radius + 22) * Math.cos(angle)}
              y={center + (radius + 22) * Math.sin(angle)}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={s.color}
              fontSize="11"
              fontWeight="bold"
            >
              {s.label}
            </text>
          );
        })}
      </svg>

      {stats2 && (
        <div
          style={{
            position: "absolute",
            bottom: -28,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: "16px",
            fontSize: "11px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: color1 }} />
            <span style={{ color: color1 }}>{label1}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: color2 }} />
            <span style={{ color: color2 }}>{label2}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// 2) SKILL COMBO CALCULATOR
// =====================================================================

export const SkillComboCalculator = ({ patch, HeroSelector, getHeroImageUrl, ItemShopPicker }) => {
  const heroes = useMemo(
    () => [...(patch?.heroes || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [patch]
  );
  const skills = patch?.skills || [];
  const items = patch?.items || [];
  const arcanas = patch?.runes || [];

  const [heroName, setHeroName] = useState(heroes[0]?.name || "");
  const [level, setLevel] = useState(15);
  const [selectedItems, setSelectedItems] = useState([]);
  const [arcanaSet, setArcanaSet] = useState({ red: null, purple: null, green: null });
  const [selectedSkills, setSelectedSkills] = useState([]);

  const hero = useMemo(() => heroes.find((h) => h.name === heroName), [heroes, heroName]);
  const levels = useMemo(() => getHeroLevels(hero), [hero]);

  useEffect(() => {
    if (levels.length && !levels.includes(level)) {
      setLevel(levels[levels.length - 1] || 1);
    }
  }, [levels, level]);

  const heroLv = useMemo(() => getHeroLevelObj(hero, level), [hero, level]);
  const heroSkills = useMemo(() => skills.filter((s) => s.hero === heroName), [skills, heroName]);

  const normItems = useMemo(() => normalizeSelected(selectedItems, items), [selectedItems, items]);
  const arcanaStats = useMemo(() => sumArcanaStats(arcanaSet, 10), [arcanaSet]);

  const totalStats = useMemo(() => {
    const base = computeFinalStats(heroLv, normItems, []) || {};
    for (const [k, v] of Object.entries(arcanaStats)) {
      base[k] = (+base[k] || 0) + v;
    }
    return base;
  }, [heroLv, normItems, arcanaStats]);

  const toggleSkill = (skill) => {
    setSelectedSkills((prev) => {
      const exists = prev.find((s) => s.name === skill.name);
      if (exists) return prev.filter((s) => s.name !== skill.name);
      return [...prev, skill];
    });
  };

  const calcSkillDamage = (skill, rank = 1) => {
    const base = skill.base_dmg?.[Math.max(0, Math.min(5, rank - 1))] || 0;
    const adScale = +skill.ad_scaling || 0;
    const apScale = +skill.ap_scaling || 0;
    const atk = +totalStats.phys_atk || 0;
    const ap = +totalStats.magic_power || 0;
    return base + adScale * atk + apScale * ap;
  };

  const totalComboDamage = useMemo(() => {
    return selectedSkills.reduce((sum, skill) => sum + calcSkillDamage(skill, 6), 0);
  }, [selectedSkills, totalStats]);

  const ArcanaSelector = () => (
    <Card style={{ padding: 12, marginBottom: 14 }}>
      <div style={{ fontWeight: 900, color: "#ffd7dc", marginBottom: 10, fontSize: 12 }}>
        Arcana (x10 each)
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {Object.entries(ARCANA_COLORS).map(([colorKey, config]) => {
          const list = filterArcana(arcanas, colorKey);
          const current = arcanaSet[colorKey.toLowerCase()];
          const Icon = config.icon;

          return (
            <div key={colorKey} style={{ flex: 1, minWidth: 160 }}>
              <div style={{ color: config.color, fontWeight: 900, marginBottom: 6, display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                <Icon size={12} /> {config.label}
              </div>
              <select
                value={current?.name || ""}
                onChange={(e) => {
                  const val = list.find((x) => x.name === e.target.value) || null;
                  setArcanaSet((prev) => ({ ...prev, [colorKey.toLowerCase()]: val }));
                }}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  background: "rgba(0,0,0,0.6)",
                  border: `1px solid ${config.color}50`,
                  borderRadius: 8,
                  color: "#fff",
                  fontSize: 12,
                  fontFamily: "'Rajdhani', sans-serif",
                }}
              >
                <option value="">- Empty -</option>
                {list.map((a) => (
                  <option key={a.name} value={a.name}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </Card>
  );

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card neon style={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <Zap size={18} color="#ff1026" />
          <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 16 }}>
            SKILL COMBO CALCULATOR
          </span>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <HeroSelector
              heroes={heroes}
              value={heroName}
              onChange={(e) => {
                setHeroName(e.target.value);
                setSelectedSkills([]);
              }}
              label="Hero"
              getHeroImageUrl={getHeroImageUrl}
            />
          </div>
          <div style={{ width: 100 }}>
            <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6, fontSize: 12 }}>Level</div>
            <Select
              value={level}
              onChange={(e) => setLevel(+e.target.value)}
              options={levels.map((lv) => ({ value: lv, label: `Lv ${lv}` }))}
            />
          </div>
        </div>

        <ArcanaSelector />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {/* Skills */}
          <Card style={{ padding: 12 }}>
            <div style={{ fontWeight: 900, color: "#ffd7dc", marginBottom: 10, fontSize: 12 }}>
              Skills (click to add to combo)
            </div>
            <div style={{ display: "grid", gap: 8, maxHeight: 300, overflow: "auto" }}>
              {heroSkills.map((skill) => {
                const isSelected = selectedSkills.some((s) => s.name === skill.name);
                const dmg = calcSkillDamage(skill, 6);
                return (
                  <button
                    key={skill.name}
                    onClick={() => toggleSkill(skill)}
                    style={{
                      padding: 10,
                      borderRadius: 10,
                      border: isSelected ? "2px solid #ff1026" : "1px solid rgba(255,16,38,0.2)",
                      background: isSelected ? "rgba(255,16,38,0.15)" : "rgba(0,0,0,0.4)",
                      cursor: "pointer",
                      textAlign: "left",
                      color: "#fff",
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                  >
                    <div style={{ fontWeight: 900, marginBottom: 4 }}>{skill.name}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {skill.slot} • {skill.damage_type || "Unknown"} • Base: {skill.base_dmg?.[5] || 0}
                    </div>
                    <div style={{ fontSize: 11, color: "#ffd7dc" }}>
                      AD: {skill.ad_scaling} • AP: {skill.ap_scaling} • Est. Dmg: {Math.round(dmg).toLocaleString()}
                    </div>
                  </button>
                );
              })}
              {heroSkills.length === 0 && (
                <div style={{ color: "#6b7280", padding: 10 }}>No skills found for this hero</div>
              )}
            </div>
          </Card>

          {/* Combo Result */}
          <Card style={{ padding: 12 }}>
            <div style={{ fontWeight: 900, color: "#ffd7dc", marginBottom: 10, fontSize: 12 }}>
              Combo ({selectedSkills.length} skills)
            </div>

            {selectedSkills.length === 0 ? (
              <div style={{ color: "#6b7280", padding: 10 }}>Click skills to build combo</div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {selectedSkills.map((skill, idx) => (
                  <div
                    key={skill.name}
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      background: "rgba(255,16,38,0.1)",
                      border: "1px solid rgba(255,16,38,0.3)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>
                      {idx + 1}. {skill.name}
                    </span>
                    <span style={{ color: "#ff1026", fontWeight: 900 }}>
                      {Math.round(calcSkillDamage(skill, 6)).toLocaleString()}
                    </span>
                  </div>
                ))}

                <div
                  style={{
                    marginTop: 10,
                    padding: 12,
                    borderRadius: 10,
                    background: "linear-gradient(135deg, rgba(255,16,38,0.2), rgba(0,0,0,0.4))",
                    border: "2px solid #ff1026",
                  }}
                >
                  <div style={{ color: "#9ca3af", fontSize: 11 }}>Total Combo Damage (Raw)</div>
                  <div style={{ fontFamily: "'Orbitron'", fontWeight: 900, fontSize: 24, color: "#ff1026" }}>
                    {Math.round(totalComboDamage).toLocaleString()}
                  </div>
                </div>

                <Button variant="danger" onClick={() => setSelectedSkills([])} style={{ marginTop: 8 }}>
                  Clear Combo
                </Button>
              </div>
            )}
          </Card>
        </div>
      </Card>

      {/* Item Shop */}
      {ItemShopPicker && (
        <ItemShopPicker items={items} selected={selectedItems} setSelected={setSelectedItems} title="Build Items" />
      )}

      {/* Stats Summary */}
      <Card style={{ padding: 12 }}>
        <div style={{ fontWeight: 900, color: "#ffd7dc", marginBottom: 10, fontSize: 12 }}>
          Current Stats (Hero + Items + Arcana)
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
          {["hp", "phys_atk", "magic_power", "phys_def", "magic_def", "armor_pierce", "magic_pierce", "crit_rate", "attack_speed"].map((key) => (
            <div
              key={key}
              style={{
                padding: 8,
                borderRadius: 8,
                background: "rgba(0,0,0,0.4)",
                border: "1px solid rgba(255,16,38,0.15)",
              }}
            >
              <div style={{ color: "#ffd7dc", fontSize: 10, fontWeight: 900 }}>{STAT_LABELS[key] || key}</div>
              <div style={{ fontFamily: "monospace", fontWeight: 900, color: "#fff" }}>
                {Math.round(totalStats[key] || 0).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// =====================================================================
// 3) HERO COMPARE TAB (ALL-IN-ONE)
// =====================================================================

export const HeroCompareTab = ({ patch, getHeroImageUrl, HeroSelector, ItemShopPicker }) => {
  const heroes = useMemo(
    () => [...(patch?.heroes || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [patch]
  );
  const items = patch?.items || [];
  const arcanas = patch?.runes || [];

  const [heroA, setHeroA] = useState(heroes[0]?.name || "");
  const [heroB, setHeroB] = useState(heroes[1]?.name || heroes[0]?.name || "");
  const [levelA, setLevelA] = useState(15);
  const [levelB, setLevelB] = useState(15);

  const [itemsA, setItemsA] = useState([]);
  const [itemsB, setItemsB] = useState([]);
  const [arcanaA, setArcanaA] = useState({ red: null, purple: null, green: null });
  const [arcanaB, setArcanaB] = useState({ red: null, purple: null, green: null });

  const [simResult, setSimResult] = useState(null);

  const heroObjA = useMemo(() => heroes.find((h) => h.name === heroA) || null, [heroes, heroA]);
  const heroObjB = useMemo(() => heroes.find((h) => h.name === heroB) || null, [heroes, heroB]);
  const levelsA = useMemo(() => getHeroLevels(heroObjA), [heroObjA]);
  const levelsB = useMemo(() => getHeroLevels(heroObjB), [heroObjB]);

  useEffect(() => {
    if (levelsA.length && !levelsA.includes(levelA)) {
      setLevelA(levelsA[levelsA.length - 1] || 1);
    }
  }, [levelsA, levelA]);

  useEffect(() => {
    if (levelsB.length && !levelsB.includes(levelB)) {
      setLevelB(levelsB[levelsB.length - 1] || 1);
    }
  }, [levelsB, levelB]);

  const baseA = useMemo(() => getHeroLevelObj(heroObjA, levelA), [heroObjA, levelA]);
  const baseB = useMemo(() => getHeroLevelObj(heroObjB, levelB), [heroObjB, levelB]);

  const normItemsA = useMemo(() => normalizeSelected(itemsA, items), [itemsA, items]);
  const normItemsB = useMemo(() => normalizeSelected(itemsB, items), [itemsB, items]);

  const arcanaStatsA = useMemo(() => sumArcanaStats(arcanaA, 10), [arcanaA]);
  const arcanaStatsB = useMemo(() => sumArcanaStats(arcanaB, 10), [arcanaB]);

  const statsA = useMemo(() => {
    const base = computeFinalStats(baseA, normItemsA, []) || {};
    for (const [k, v] of Object.entries(arcanaStatsA)) {
      base[k] = (+base[k] || 0) + v;
    }
    return base;
  }, [baseA, normItemsA, arcanaStatsA]);

  const statsB = useMemo(() => {
    const base = computeFinalStats(baseB, normItemsB, []) || {};
    for (const [k, v] of Object.entries(arcanaStatsB)) {
      base[k] = (+base[k] || 0) + v;
    }
    return base;
  }, [baseB, normItemsB, arcanaStatsB]);

  const compareKeys = [
    "hp", "phys_atk", "magic_power", "phys_def", "magic_def", "move_speed",
    "armor_pierce", "armor_pierce_pct", "magic_pierce", "magic_pierce_pct",
    "attack_speed", "crit_rate", "crit_dmg", "life_steal", "cooldown_reduction",
  ];

  const imgA = getHeroImageUrl?.(heroA);
  const imgB = getHeroImageUrl?.(heroB);

  const RoleIconA = ROLES[heroObjA?.role]?.icon || Users;
  const RoleIconB = ROLES[heroObjB?.role]?.icon || Users;

  const resetBuilds = () => {
    setItemsA([]);
    setItemsB([]);
    setArcanaA({ red: null, purple: null, green: null });
    setArcanaB({ red: null, purple: null, green: null });
    setSimResult(null);
  };

  const safeApplyDefenseWithPen = useCallback((raw, def, penFlat, penPct) => {
    if (typeof applyDefenseWithPenetration === "function") {
      try {
        if (applyDefenseWithPenetration.length >= 4) return applyDefenseWithPenetration(raw, def, penFlat, penPct);
        if (applyDefenseWithPenetration.length === 3) return applyDefenseWithPenetration(raw, def, { penFlat, penPct });
        return applyDefenseWithPenetration(raw, def);
      } catch (e) {}
    }
    const defAdj = Math.max(0, (def || 0) * (1 - (penPct || 0) / 100) - (penFlat || 0));
    return applyDefenseSimple(raw, defAdj);
  }, []);

  const calcAA_DPS = useCallback((att, def, type = "PHYS") => {
    const atk = +att?.phys_atk || 0;
    const ap = +att?.magic_power || 0;
    const rawHit = type === "MAGIC" ? ap : atk;
    const asPct = +att?.attack_speed || 0;
    const attacksPerSec = 1.0 * (1 + asPct / 100);
    const critRate = Math.max(0, Math.min(100, +att?.crit_rate || 0));
    const critDmgPct = +att?.crit_dmg || 200;
    const critMult = 1 + (critRate / 100) * Math.max(0, (critDmgPct / 100) - 1);
    const penFlat = type === "MAGIC" ? (+att?.magic_pierce || 0) : (+att?.armor_pierce || 0);
    const penPct = type === "MAGIC" ? (+att?.magic_pierce_pct || 0) : (+att?.armor_pierce_pct || 0);
    const defVal = type === "MAGIC" ? (+def?.magic_def || 0) : (+def?.phys_def || 0);
    const finalHit = safeApplyDefenseWithPen(rawHit, defVal, penFlat, penPct);
    const dps = finalHit * attacksPerSec * critMult;
    return { rawHit, finalHit, attacksPerSec, critMult, dps };
  }, [safeApplyDefenseWithPen]);

  const simulate1v1 = () => {
    const hpA = Math.max(1, +statsA?.hp || 1);
    const hpB = Math.max(1, +statsB?.hp || 1);
    const aIntoB = calcAA_DPS(statsA, statsB, "PHYS");
    const bIntoA = calcAA_DPS(statsB, statsA, "PHYS");
    const ttkA = aIntoB.dps > 0 ? hpB / aIntoB.dps : Infinity;
    const ttkB = bIntoA.dps > 0 ? hpA / bIntoA.dps : Infinity;
    let winner = "Tie";
    if (ttkA < ttkB) winner = heroA || "Hero A";
    if (ttkB < ttkA) winner = heroB || "Hero B";
    setSimResult({ winner, ttkA, ttkB, aIntoB, bIntoA, hpA, hpB });
  };

  // =====================================================================
  // ARCANA ROW COMPONENT
  // =====================================================================

  const ArcanaRow = ({ side }) => {
    const isA = side === "A";
    const accent = isA ? "#ff1026" : "#22c55e";
    const arcanaSet = isA ? arcanaA : arcanaB;
    const setArcana = isA ? setArcanaA : setArcanaB;
    const heroName = isA ? heroA : heroB;

    const SelectBox = ({ colorKey }) => {
      const config = ARCANA_COLORS[colorKey];
      const list = useMemo(() => filterArcana(arcanas, colorKey), [colorKey]);
      const current = arcanaSet[colorKey.toLowerCase()];
      const Icon = config.icon;

      return (
        <div style={{ flex: 1, minWidth: 160 }}>
          <div
            style={{
              color: config.color,
              fontWeight: 900,
              marginBottom: 6,
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
            }}
          >
            <Icon size={12} /> {config.label} (x10)
          </div>
          <select
            value={current?.name || ""}
            onChange={(e) => {
              const val = list.find((x) => x.name === e.target.value) || null;
              setArcana((prev) => ({ ...prev, [colorKey.toLowerCase()]: val }));
            }}
            style={{
              width: "100%",
              padding: "8px 10px",
              background: "rgba(0,0,0,0.6)",
              border: `1px solid ${config.color}50`,
              borderRadius: 8,
              color: "#fff",
              fontSize: 12,
              fontFamily: "'Rajdhani', sans-serif",
            }}
          >
            <option value="">- Empty -</option>
            {list.map((a) => (
              <option key={a.name} value={a.name}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      );
    };

    return (
      <Card style={{ padding: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontWeight: 900,
              fontFamily: "'Orbitron'",
              fontSize: 12,
              color: accent,
            }}
          >
            {heroName || (isA ? "Hero A" : "Hero B")} Arcana
          </div>
          <Button
            variant="danger"
            onClick={() => setArcana({ red: null, purple: null, green: null })}
            style={{ padding: "6px 10px", borderRadius: 8, fontSize: 11 }}
          >
            Clear
          </Button>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <SelectBox colorKey="Red" />
          <SelectBox colorKey="Purple" />
          <SelectBox colorKey="Green" />
        </div>

        <div style={{ marginTop: 10, fontSize: 11, color: "#6b7280" }}>
          {(() => {
            const sum = sumArcanaStats(arcanaSet, 10);
            const keys = Object.keys(sum).filter((k) => sum[k] > 0);
            if (!keys.length) return <span>No arcana selected</span>;
            return (
              <span style={{ color: "#9ca3af" }}>
                Total: {keys.map((k) => `${STAT_LABELS[k] || k} +${Math.round(sum[k])}`).join(", ")}
              </span>
            );
          })()}
        </div>
      </Card>
    );
  };

  // =====================================================================
  // BUILD PANEL
  // =====================================================================

  const BuildPanel = () => {
    const missingPicker = !ItemShopPicker;
    return (
      <div style={{ display: "grid", gap: 14 }}>
        {missingPicker && (
          <Card neon style={{ padding: 12, color: "#fff" }}>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>ItemShopPicker not provided</div>
            <div style={{ color: "#9ca3af", fontSize: 13 }}>
              Pass <code style={{ color: "#ffd7dc" }}>ItemShopPicker</code> into HeroCompareTab
            </div>
          </Card>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Card style={{ padding: 12 }}>
            <div
              style={{
                fontWeight: 900,
                fontFamily: "'Orbitron'",
                fontSize: 12,
                color: "#ff1026",
                marginBottom: 8,
              }}
            >
              {heroA || "Hero A"} Items
            </div>
            {ItemShopPicker && (
              <ItemShopPicker items={items} selected={itemsA} setSelected={setItemsA} title="" compact />
            )}
          </Card>

          <Card style={{ padding: 12 }}>
            <div
              style={{
                fontWeight: 900,
                fontFamily: "'Orbitron'",
                fontSize: 12,
                color: "#22c55e",
                marginBottom: 8,
              }}
            >
              {heroB || "Hero B"} Items
            </div>
            {ItemShopPicker && (
              <ItemShopPicker items={items} selected={itemsB} setSelected={setItemsB} title="" compact />
            )}
          </Card>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <ArcanaRow side="A" />
          <ArcanaRow side="B" />
        </div>
      </div>
    );
  };

  // =====================================================================
  // COMPARE PANEL
  // =====================================================================

  const ComparePanel = () => (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 340px) 1fr", gap: 14 }}>
      <Card style={{ padding: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <RadarChart
          stats={statsA}
          stats2={statsB}
          size={280}
          label1={heroA}
          label2={heroB}
          color1="#ff1026"
          color2="#22c55e"
        />
      </Card>

      <Card style={{ padding: 12 }}>
        <div style={{ fontWeight: 900, color: "#ffd7dc", marginBottom: 10, fontSize: 12 }}>
          Stats Comparison (includes Items + Arcana)
        </div>

        <div style={{ maxHeight: 360, overflow: "auto", display: "grid", gap: 6 }}>
          {compareKeys.map((key) => {
            const vA = +statsA[key] || 0;
            const vB = +statsB[key] || 0;
            const max = Math.max(vA, vB, 1);
            const diff = vA - vB;

            return (
              <div
                key={key}
                style={{
                  padding: 8,
                  borderRadius: 10,
                  background: "rgba(0,0,0,0.4)",
                  border: "1px solid rgba(255,16,38,0.12)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: "#ffd7dc", fontWeight: 900, fontSize: 12 }}>
                    {STAT_LABELS[key] || key}
                  </span>

                  <span
                    style={{
                      fontFamily: "monospace",
                      fontWeight: 900,
                      fontSize: 11,
                      color: diff > 0 ? "#ff1026" : diff < 0 ? "#22c55e" : "#6b7280",
                    }}
                  >
                    {diff !== 0 ? (diff > 0 ? `+${Math.round(diff)}` : Math.round(diff)) : "="}
                  </span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div
                      style={{
                        width: 52,
                        textAlign: "right",
                        fontFamily: "monospace",
                        fontSize: 11,
                        color: "#ff1026",
                      }}
                    >
                      {Math.round(vA).toLocaleString()}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        height: 6,
                        background: "rgba(0,0,0,0.6)",
                        borderRadius: 3,
                        overflow: "hidden",
                      }}
                    >
                      <div style={{ width: `${(vA / max) * 100}%`, height: "100%", background: "#ff1026" }} />
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div
                      style={{
                        flex: 1,
                        height: 6,
                        background: "rgba(0,0,0,0.6)",
                        borderRadius: 3,
                        overflow: "hidden",
                      }}
                    >
                      <div style={{ width: `${(vB / max) * 100}%`, height: "100%", background: "#22c55e" }} />
                    </div>
                    <div style={{ width: 52, fontFamily: "monospace", fontSize: 11, color: "#22c55e" }}>
                      {Math.round(vB).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );

  // =====================================================================
  // SUMMARY PANEL
  // =====================================================================

  const SummaryPanel = () => {
    const aWins = compareKeys.filter((k) => (+statsA[k] || 0) > (+statsB[k] || 0)).length;
    const bWins = compareKeys.filter((k) => (+statsB[k] || 0) > (+statsA[k] || 0)).length;
    const ties = compareKeys.length - aWins - bWins;

    return (
      <Card style={{ padding: 12 }}>
        <div style={{ fontWeight: 900, color: "#ffd7dc", marginBottom: 10, fontSize: 12 }}>Summary</div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          <Card style={{ padding: 10, border: "2px solid rgba(255,16,38,0.4)", textAlign: "center" }}>
            <div style={{ color: "#ff1026", fontWeight: 900, fontSize: 11 }}>{heroA}</div>
            <div style={{ fontSize: 24, fontWeight: 900, fontFamily: "'Orbitron'", color: "#ff1026" }}>{aWins}</div>
          </Card>

          <Card style={{ padding: 10, border: "2px solid rgba(107,114,128,0.4)", textAlign: "center" }}>
            <div style={{ color: "#6b7280", fontWeight: 900, fontSize: 11 }}>Tie</div>
            <div style={{ fontSize: 24, fontWeight: 900, fontFamily: "'Orbitron'", color: "#6b7280" }}>{ties}</div>
          </Card>

          <Card style={{ padding: 10, border: "2px solid rgba(34,197,94,0.4)", textAlign: "center" }}>
            <div style={{ color: "#22c55e", fontWeight: 900, fontSize: 11 }}>{heroB}</div>
            <div style={{ fontSize: 24, fontWeight: 900, fontFamily: "'Orbitron'", color: "#22c55e" }}>{bWins}</div>
          </Card>
        </div>
      </Card>
    );
  };

  // =====================================================================
  // SIM PANEL
  // =====================================================================

  const SimPanel = () => (
    <div style={{ display: "grid", gap: 14 }}>
      <Card style={{ padding: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontWeight: 900, fontFamily: "'Orbitron'", letterSpacing: 2, color: "#ffd7dc" }}>
              1v1 Simulator (Basic TTK)
            </div>
            <div style={{ color: "#6b7280", fontSize: 12 }}>
              ใช้ stats ปัจจุบัน (รวม Items + Arcana) • คิด AA DPS + DEF
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button onClick={simulate1v1}>
              <Users size={16} /> Simulate
            </Button>
            <Button variant="secondary" onClick={() => setSimResult(null)}>
              <RotateCcw size={16} /> Clear
            </Button>
          </div>
        </div>
      </Card>

      {simResult ? (
        <Card neon style={{ padding: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div
              style={{
                padding: 12,
                borderRadius: 12,
                background: "rgba(0,0,0,0.35)",
                border: "1px solid rgba(255,16,38,0.2)",
              }}
            >
              <div style={{ fontWeight: 900, color: "#ff1026", marginBottom: 6 }}>
                {heroA} → {heroB}
              </div>
              <div style={{ color: "#9ca3af", fontSize: 12 }}>
                HP(B): <span style={{ color: "#fff", fontWeight: 900 }}>{Math.round(simResult.hpB).toLocaleString()}</span>
              </div>
              <div style={{ color: "#9ca3af", fontSize: 12 }}>
                DPS: <span style={{ color: "#fff", fontWeight: 900 }}>{Math.round(simResult.aIntoB.dps).toLocaleString()}</span>
              </div>
              <div style={{ color: "#9ca3af", fontSize: 12 }}>
                TTK:{" "}
                <span style={{ color: "#fff", fontWeight: 900 }}>
                  {Number.isFinite(simResult.ttkA) ? `${simResult.ttkA.toFixed(2)}s` : "∞"}
                </span>
              </div>
            </div>

            <div
              style={{
                padding: 12,
                borderRadius: 12,
                background: "rgba(0,0,0,0.35)",
                border: "1px solid rgba(34,197,94,0.2)",
              }}
            >
              <div style={{ fontWeight: 900, color: "#22c55e", marginBottom: 6 }}>
                {heroB} → {heroA}
              </div>
              <div style={{ color: "#9ca3af", fontSize: 12 }}>
                HP(A): <span style={{ color: "#fff", fontWeight: 900 }}>{Math.round(simResult.hpA).toLocaleString()}</span>
              </div>
              <div style={{ color: "#9ca3af", fontSize: 12 }}>
                DPS: <span style={{ color: "#fff", fontWeight: 900 }}>{Math.round(simResult.bIntoA.dps).toLocaleString()}</span>
              </div>
              <div style={{ color: "#9ca3af", fontSize: 12 }}>
                TTK:{" "}
                <span style={{ color: "#fff", fontWeight: 900 }}>
                  {Number.isFinite(simResult.ttkB) ? `${simResult.ttkB.toFixed(2)}s` : "∞"}
                </span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: "rgba(0,0,0,0.45)" }}>
            <div style={{ color: "#9ca3af", fontSize: 12 }}>Winner</div>
            <div style={{ fontFamily: "'Orbitron'", fontWeight: 900, fontSize: 18, color: "#ffd7dc" }}>
              {simResult.winner}
            </div>
            <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
              *โมเดลนี้เป็นแบบง่าย (AA only) ถ้าคุณอยากให้รวมสกิล/คูลดาวน์ บอกผมได้ เดี๋ยวจัดโครงให้
            </div>
          </div>
        </Card>
      ) : (
        <Card style={{ padding: 16, color: "#6b7280" }}>
          กด <b style={{ color: "#ffd7dc" }}>Simulate</b> เพื่อดู TTK (Time-to-Kill) ของทั้งสองฝั่ง
        </Card>
      )}
    </div>
  );

  // =====================================================================
  // MAIN RENDER
  // =====================================================================

  if (!patch || !heroes.length) {
    return (
      <div style={{ padding: 16, color: "#9ca3af" }}>
        No Patch Data (upload Excel first)
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {/* HEADER / HERO PICK */}
      <Card neon style={{ padding: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <GitCompare size={18} color="#ff1026" />
            <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 16 }}>
              HERO COMPARE (ALL-IN-ONE)
            </span>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button variant="secondary" onClick={resetBuilds}>
              <RotateCcw size={16} /> Reset Build
            </Button>
            <Button onClick={simulate1v1}>
              <Users size={16} /> Quick 1v1
            </Button>
          </div>
        </div>

        <div
          style={{
            marginTop: 14,
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            gap: 16,
            alignItems: "center",
          }}
        >
          {/* Hero A */}
          <div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 12,
                  overflow: "hidden",
                  border: "3px solid #ff1026",
                  background: "rgba(0,0,0,0.6)",
                }}
              >
                {imgA ? (
                  <img src={imgA} alt={heroA} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Users size={24} color="#ff1026" />
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontFamily: "'Orbitron'", fontWeight: 900, fontSize: 16, color: "#ff1026" }}>
                  {heroA || "Select"}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#6b7280", fontSize: 11 }}>
                  <RoleIconA size={12} color={ROLES[heroObjA?.role]?.color} />
                  {heroObjA?.role || "-"}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 6 }}>
              <div style={{ flex: 1 }}>
                <HeroSelector
                  heroes={heroes}
                  value={heroA}
                  onChange={(e) => setHeroA(e.target.value)}
                  label=""
                  getHeroImageUrl={getHeroImageUrl}
                />
              </div>
              <div style={{ width: 90 }}>
                <Select
                  value={levelA}
                  onChange={(e) => setLevelA(+e.target.value)}
                  options={levelsA.map((lv) => ({ value: lv, label: `Lv${lv}` }))}
                />
              </div>
            </div>
          </div>

          {/* VS */}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #ff1026, #a30013)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'Orbitron'",
              fontWeight: 900,
              fontSize: 14,
              color: "#fff",
              boxShadow: "0 0 20px rgba(255,16,38,0.6)",
            }}
          >
            VS
          </div>

          {/* Hero B */}
          <div>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                marginBottom: 10,
                justifyContent: "flex-end",
              }}
            >
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'Orbitron'", fontWeight: 900, fontSize: 16, color: "#22c55e" }}>
                  {heroB || "Select"}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: "#6b7280",
                    fontSize: 11,
                    justifyContent: "flex-end",
                  }}
                >
                  <RoleIconB size={12} color={ROLES[heroObjB?.role]?.color} />
                  {heroObjB?.role || "-"}
                </div>
              </div>

              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 12,
                  overflow: "hidden",
                  border: "3px solid #22c55e",
                  background: "rgba(0,0,0,0.6)",
                }}
              >
                {imgB ? (
                  <img src={imgB} alt={heroB} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Users size={24} color="#22c55e" />
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 6 }}>
              <div style={{ flex: 1 }}>
                <HeroSelector
                  heroes={heroes}
                  value={heroB}
                  onChange={(e) => setHeroB(e.target.value)}
                  label=""
                  getHeroImageUrl={getHeroImageUrl}
                />
              </div>
              <div style={{ width: 90 }}>
                <Select 
                  value={levelB}
                  onChange={(e) => setLevelB(+e.target.value)}
                  options={levelsB.map((lv) => ({ value: lv, label: `Lv${lv}` }))}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* ALL-IN-ONE SECTIONS */}
      <Card style={{ padding: 12 }}>
        <div
          style={{
            fontWeight: 900,
            color: "#ffd7dc",
            marginBottom: 10,
            fontFamily: "'Orbitron'",
            letterSpacing: 2,
          }}
        >
          COMPARE
        </div>
        <ComparePanel />
        <div style={{ marginTop: 14 }}>
          <SummaryPanel />
        </div>
      </Card>

      <Card style={{ padding: 12 }}>
        <div
          style={{
            fontWeight: 900,
            color: "#ffd7dc",
            marginBottom: 10,
            fontFamily: "'Orbitron'",
            letterSpacing: 2,
          }}
        >
          BUILD (ITEMS + ARCANA)
        </div>
        <BuildPanel />
      </Card>

      <Card style={{ padding: 12 }}>
        <div
          style={{
            fontWeight: 900,
            color: "#ffd7dc",
            marginBottom: 10,
            fontFamily: "'Orbitron'",
            letterSpacing: 2,
          }}
        >
          1V1
        </div>
        <SimPanel />
      </Card>
    </div>
  );
};

export default { RadarChart, SkillComboCalculator, HeroCompareTab };
